import { useEffect, useMemo, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import LocationPicker from "@/components/LocationPicker";
import { formatRupiah, CUST_STATUS_LABEL, canBillingManage, canBillingAdminOnly } from "@/lib/billing";
import { Plus, Pencil, MapPin, Ban, CheckCircle, Trash2, X, CalendarDays, AlertTriangle } from "lucide-react";
import { highlightMatch } from "@/lib/highlight";
import { CustomerOdpSection } from "@/components/network/CustomerOdpSection";

/**
 * Aug 2026 Phase 18 — Compute jatuh-tempo status untuk Customer Master
 * berdasarkan invoice outstanding + fallback rolling.
 *
 *   - Ada current_invoice dengan sisa > 0:
 *       * jt > today → HIJAU  "25 Sep 2026"
 *       * jt == today → KUNING "Hari ini · 25 Sep 2026"
 *       * jt < today  → MERAH  "Terlambat · 25 Sep 2026"
 *   - Tidak ada outstanding (semua LUNAS): HIJAU "Berikutnya · 25 {bulan+1
 *     dari last_paid_periode} YYYY" — memakai customer.jatuh_tempo
 *     day-of-month. Rolling recurring.
 *   - Tidak ada invoice sama sekali & tidak ada customer.jatuh_tempo:
 *     ABU-ABU "Tidak ada data".
 * `customer.jatuh_tempo` HANYA dipakai sebagai day-of-month untuk
 * merender label rolling ke bulan berikutnya; TIDAK PERNAH dipakai
 * sebagai penentu overdue.
 */
const MONTH_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function fmtDateID(iso) {
  // iso = "YYYY-MM-DD"
  if (!iso || typeof iso !== "string") return "";
  const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
  if (!y || !m || !d) return iso;
  return `${d} ${MONTH_ID[m - 1] || m} ${y}`;
}

function fmtPeriodeToDueLabel(periode, jt) {
  // periode = "YYYY-MM", jt = day-of-month (1-28)
  if (!periode || !jt) return "";
  const [y, m] = periode.split("-").map((s) => parseInt(s, 10));
  if (!y || !m) return "";
  const day = Math.min(parseInt(jt, 10) || 1, 28);
  return `${day} ${MONTH_ID[m - 1]} ${y}`;
}

function nextPeriodeFrom(periode) {
  // periode = "YYYY-MM" → "YYYY-MM" bulan berikutnya
  if (!periode) return null;
  const [y, m] = periode.split("-").map((s) => parseInt(s, 10));
  if (!y || !m) return null;
  const dt = new Date(y, m - 1 + 1, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function jatuhTempoBadge(c) {
  // c = customer row hasil enrichment backend (list_customers).
  const sisa = c?.current_invoice_sisa_tagihan || 0;
  const totalSisa = c?.total_sisa_tagihan || 0;
  const jtISO = c?.current_invoice_tanggal_jatuh_tempo;
  const jtDay = c?.jatuh_tempo;

  // ----- OUTSTANDING (masih ada sisa) — invoice tertua dari backend -----
  // Prioritas utama sesuai Phase 18 bug fix: selalu tampilkan invoice
  // outstanding tertua, JANGAN roll ke bulan berikutnya bila masih ada
  // sisa_tagihan > 0.
  if (sisa > 0 && jtISO) {
    const today = todayISO();
    const dateStr = fmtDateID(jtISO);
    if (jtISO > today) return { label: `Jatuh Tempo · ${dateStr}`, cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" };
    if (jtISO === today) return { label: `Jatuh Tempo Hari Ini · ${dateStr}`, cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" };
    return { label: `Terlambat · ${dateStr}`, cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]" };
  }
  // Outstanding tapi tanggal jatuh tempo invoice = null (invoice lama) →
  // fallback derive ISO dari periode invoice + customer.jatuh_tempo.
  if (sisa > 0 && !jtISO && jtDay && c?.current_invoice_periode) {
    const today = todayISO();
    const [y, m] = c.current_invoice_periode.split("-");
    const d = String(Math.min(parseInt(jtDay, 10), 28)).padStart(2, "0");
    const derivedISO = `${y}-${m}-${d}`;
    const dateStr = fmtDateID(derivedISO);
    if (derivedISO > today) return { label: `Jatuh Tempo · ${dateStr}`, cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" };
    if (derivedISO === today) return { label: `Jatuh Tempo Hari Ini · ${dateStr}`, cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" };
    return { label: `Terlambat · ${dateStr}`, cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]" };
  }

  // ----- TIDAK ADA OUTSTANDING (sisa == 0) -----
  // Sep 2026 Phase 21 — gatekeeper tambahan: bila `total_sisa_tagihan > 0`
  // (masih ada tunggakan agregat meski current_invoice tidak ter-set),
  // JANGAN pakai label "Berikutnya" — itu misleading. Fall through ke
  // ABU-ABU "Tidak ada data" biar operator sadar ada anomali data.
  if (totalSisa > 0) {
    return { label: "Perlu Cek Data · Tunggakan Aktif", cls: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]" };
  }
  // Hanya di sini boleh rolling ke periode berikutnya sebagai "Berikutnya".
  if (c?.last_paid_periode && jtDay) {
    const nextP = nextPeriodeFrom(c.last_paid_periode);
    const label = fmtPeriodeToDueLabel(nextP, jtDay);
    if (label) return { label: `Berikutnya · ${label}`, cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" };
  }
  if (jtDay) {
    return { label: `Belum ada tagihan · Tgl ${jtDay}`, cls: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]" };
  }
  return { label: "Tidak ada data", cls: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]" };
}

function JatuhTempoBadge({ c, testid }) {
  const b = jatuhTempoBadge(c);
  return (
    <span data-testid={testid}
          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap ${b.cls}`}>
      {b.label}
    </span>
  );
}

/**
 * BillingCustomers — halaman utama pengelolaan customer Billing.
 * Admin/Billing: list + filter + CRUD + ubah status layanan.
 * Kolektor: read-only list customer yang di-assign (backend scope).
 */
export default function BillingCustomers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [wilayahList, setWilayahList] = useState([]);
  const [packageList, setPackageList] = useState([]);
  const [kolektorList, setKolektorList] = useState([]);
  const [q, setQ] = useState("");
  // Sep 2026 Phase 22n — debounce 400ms untuk `q` (search server-side).
  // Auto-load setelah user berhenti mengetik supaya tidak perlu klik
  // Terapkan. Enter tetap trigger load segera.
  const [qDeb, setQDeb] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setQDeb(q), 400);
    return () => clearTimeout(h);
  }, [q]);
  const [filterWil, setFilterWil] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  // Feb 2026 — Filter koordinat customer (audit): "" = Semua, "SET" =
  // sudah diset (lat+lon valid), "UNSET" = belum diset / invalid.
  const [filterCoord, setFilterCoord] = useState("");
  // Feb 2026 — Filter kolektor (ADMIN ONLY). Kosong = "Semua Kolektor".
  const [filterCollector, setFilterCollector] = useState("");
  // Feb 2026 — Pagination default 5 rows per page (Customer Billing).
  const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];
  const [pageSize, setPageSize] = useState(5);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusEditing, setStatusEditing] = useState(null);
  // Feb 2026 — Coordinate-only dialog untuk KOLEKTOR (dan admin sebagai
  // shortcut). Terpisah dari `open`/`editing` supaya tidak membuka full
  // Edit Customer form.
  const [coordEditing, setCoordEditing] = useState(null);
  // Sep 2026 Phase 20 — Hard delete customer state (admin-only).
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Feb 2026 — Bulk Reassign Kolektor (admin tool). `selected` berisi
  // customer_id yang di-check pada halaman saat ini; otomatis bersih
  // saat page/filter berubah supaya tidak membawa stale id.
  const [selected, setSelected] = useState(new Set());
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [bulkStartOpen, setBulkStartOpen] = useState(false);
  const canManage = canBillingManage(user);
  const isAdmin = canBillingAdminOnly(user);
  const isKolektor = (user?.role || "").toLowerCase() === "kolektor";
  // Feb 2026 — Kolektor boleh set koordinat customer dalam scope-nya.
  // Backend re-validates ownership; frontend visibility hanya
  // improvement UX.
  const canSetCoord = isAdmin || isKolektor;

  const runHardDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/billing/customers/${deleting.customer_id}`);
      const invMsg = data.invoice_deleted > 0 ? ` ${data.invoice_deleted} invoice` : "";
      const payMsg = data.payment_deleted > 0 ? ` dan ${data.payment_deleted} pembayaran ikut dibersihkan` : "";
      const suffix = (invMsg || payMsg) ? `.${invMsg}${payMsg}` : ". Tidak ada invoice terkait";
      toast.success(`${data.nama_customer || deleting.nama_customer} berhasil dihapus${suffix}.`);
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Customer gagal dihapus. Tidak ada perubahan data yang diterapkan.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, page };
      if (filterWil) params.wilayah_id = filterWil;
      if (filterStatus) params.status_layanan = filterStatus;
      if (filterCollector && isAdmin) params.collector_user_id = filterCollector;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/billing/customers", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      // Feb 2026 — Bulk-select: clear setiap kali rows baru dimuat
      // (pindah halaman, filter berubah, reload setelah action). Ini
      // menghindari stale customer_id yang mungkin sudah di luar page.
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat customer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (canManage) {
      api.get("/billing/wilayah").then(({ data }) => setWilayahList(data.items || [])).catch(() => {});
      api.get("/billing/packages").then(({ data }) => setPackageList(data.items || [])).catch(() => {});
      // Feb 2026 — daftar user yang valid sebagai kolektor:
      // primary role kolektor/admin ATAU teknisi/koordinator dgn
      // extra_roles=kolektor.
      api.get("/users").then(({ data }) => {
        const users = data || [];
        setKolektorList(users.filter((u) =>
          u.role === "kolektor" || u.role === "admin" ||
          ((u.role === "teknisi" || u.role === "koordinator") &&
            (u.extra_roles || []).includes("kolektor"))
        ));
      }).catch(() => setKolektorList([]));
    }
    // eslint-disable-next-line
  }, [filterWil, filterStatus, filterCollector, qDeb, page, pageSize]);
  // Feb 2026 — Reset ke page 1 saat filter/search berubah.
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [filterWil, filterStatus, filterCollector, qDeb, pageSize]);

  // Feb 2026 — Helper koordinat: valid = numeric, dalam range, kedua
  // field terisi. URL Google Maps pakai format api=1 supaya konsisten
  // dgn shortcut Tagihan Saya.
  const isValidCoord = (c) => {
    const lat = Number(c?.latitude);
    const lon = Number(c?.longitude);
    return (
      c?.latitude !== undefined && c?.latitude !== null && c?.latitude !== "" &&
      c?.longitude !== undefined && c?.longitude !== null && c?.longitude !== "" &&
      Number.isFinite(lat) && Number.isFinite(lon) &&
      lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
    );
  };
  const mapsUrl = (c) => `https://www.google.com/maps/search/?api=1&query=${Number(c.latitude)},${Number(c.longitude)}`;

  const filtered = useMemo(() => {
    if (!filterCoord) return rows;
    return rows.filter((c) => {
      const hasCoord = isValidCoord(c);
      return filterCoord === "SET" ? hasCoord : !hasCoord;
    });
  }, [rows, filterCoord]);

  // Aug 2026 — lookup map paket_id → {nama, harga} untuk render kolom
  // Harga Paket di list. Reuse `packageList` yang sudah dimuat, TIDAK
  // membuat endpoint baru.
  const packageMap = useMemo(() => {
    const m = {};
    for (const p of packageList) {
      m[p.package_id] = { nama: p.nama_paket, harga: p.harga };
    }
    return m;
  }, [packageList]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="billing-customers-page">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Customer Billing</h1>
          <p className="text-sm text-[#6B7280]" data-testid="billing-cust-summary">
            {canManage ? "Kelola data pelanggan yang akan ditagih." : "Customer yang di-assign kepada Anda."}
            {" "}Total: {total}
            {isAdmin && filterCollector && (
              <>
                {" · "}
                <span data-testid="billing-cust-collector-active-badge">
                  Kolektor: <b className="text-[#111827]">
                    {filterCollector === "KOLEKTOR-SYSTEM"
                      ? "Kolektor Sistem"
                      : (kolektorList.find((u) => u.id === filterCollector) || {}).name
                        || (kolektorList.find((u) => u.id === filterCollector) || {}).username
                        || filterCollector}
                  </b>
                </span>
              </>
            )}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => {
              // Aug 2026 — default cerdas untuk pengalaman input baru:
              // Tanggal Mulai = hari ini, Jatuh Tempo = 25.
              const today = new Date();
              const y = today.getFullYear();
              const m = String(today.getMonth() + 1).padStart(2, "0");
              const d = String(today.getDate()).padStart(2, "0");
              setEditing({ isNew: true, jatuh_tempo: 25, tanggal_mulai: `${y}-${m}-${d}` });
              setOpen(true);
            }}
                  data-testid="billing-cust-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Tambah Customer
          </Button>
        )}
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Input placeholder="Cari nama / HP / customer ID" value={q}
                 onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 className={q ? "pr-9" : ""}
                 data-testid="billing-cust-search" />
          {q && (
            <button type="button" onClick={() => setQ("")}
                    aria-label="Bersihkan pencarian"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6]"
                    data-testid="billing-cust-search-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <Button variant="outline" onClick={load}>Terapkan</Button>
        {canManage && (
          <select value={filterWil} onChange={(e) => setFilterWil(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="billing-cust-wilayah-filter">
            <option value="">Semua Wilayah</option>
            {wilayahList.map((w) => (
              <option key={w.wilayah_id} value={w.wilayah_id}>{w.nama_wilayah}</option>
            ))}
          </select>
        )}
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="billing-cust-status-filter">
          <option value="">Semua Status</option>
          <option value="AKTIF">Aktif</option>
          <option value="ISOLIR">Isolir</option>
          <option value="BERHENTI">Berhenti</option>
          <option value="FREE">Free</option>
        </select>
        <select value={filterCoord} onChange={(e) => setFilterCoord(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="billing-cust-coord-filter">
          <option value="">Semua Koordinat</option>
          <option value="SET">Sudah Diset</option>
          <option value="UNSET">Belum Diset</option>
        </select>
        {isAdmin && (
          <select value={filterCollector} onChange={(e) => setFilterCollector(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm bg-white sm:max-w-[220px] truncate"
                  data-testid="billing-cust-collector-filter">
            <option value="">Semua Kolektor</option>
            <option value="KOLEKTOR-SYSTEM">Kolektor Sistem</option>
            {kolektorList.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.username}</option>
            ))}
          </select>
        )}
      </div>

      {/* Feb 2026 — Pagination dipindah ke panel atas supaya user tidak perlu scroll. */}
      <div className="flex items-center justify-between text-sm py-2 flex-wrap gap-2" data-testid="billing-cust-pagination">
        <div>
          {total > pageSize
            ? <>Halaman {page} dari {Math.max(1, Math.ceil(total / pageSize))} · Total {total} customer</>
            : <>Total {total} customer</>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1 text-xs text-[#6B7280]">
            Per halaman:
            <select value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border rounded-md px-2 py-1 text-xs bg-white"
                    data-testid="billing-cust-page-size">
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Sebelumnya</Button>
          <Button size="sm" variant="outline" onClick={() => setPage((p) => p + 1)} disabled={page * pageSize >= total}>Berikutnya</Button>
        </div>
      </div>

      {loading && <div className="text-sm text-[#6B7280]">Memuat…</div>}
      {!loading && filtered.length === 0 && (
        <div className="p-8 text-center text-sm text-[#6B7280] border rounded-md" data-testid="billing-cust-empty">
          Belum ada customer.
        </div>
      )}

      {/* Feb 2026 — Bulk action bar (admin only). Muncul saat ada baris
          ter-select. Sticky top supaya tetap terlihat saat tabel panjang.
          Scope select = halaman saat ini (clear otomatis saat page berubah). */}
      {canManage && isAdmin && selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-[#EEF2FF] border border-[#C7D2FE] rounded-md px-3 py-2 text-sm sticky top-14 z-10"
             data-testid="billing-cust-bulk-bar">
          <div className="font-semibold">
            {selected.size} customer terpilih
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => setBulkReassignOpen(true)}
              data-testid="billing-cust-bulk-reassign-btn"
            >
              Reassign Kolektor…
            </Button>
            <Button
              size="sm"
              onClick={() => setBulkStartOpen(true)}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
              data-testid="billing-cust-bulk-startdate-btn"
            >
              <CalendarDays className="w-4 h-4 mr-1" /> Ubah Tanggal Mulai
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelected(new Set())}
              data-testid="billing-cust-bulk-clear"
            >
              Batal Pilih
            </Button>
          </div>
        </div>
      )}

      {/* Desktop table (md+) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[900px]">
          <thead>
            <tr className="text-left border-b bg-[#F8F9FA]">
              {canManage && isAdmin && (
                <th className="px-3 py-2 w-10">
                  <input
                    type="checkbox"
                    aria-label="Pilih semua di halaman ini"
                    data-testid="billing-cust-select-all"
                    checked={filtered.length > 0 && filtered.every((c) => selected.has(c.customer_id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected(new Set(filtered.map((c) => c.customer_id)));
                      } else {
                        setSelected(new Set());
                      }
                    }}
                  />
                </th>
              )}
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2 hidden sm:table-cell">Wilayah</th>
              <th className="px-3 py-2">Paket / Harga</th>
              <th className="px-3 py-2 hidden lg:table-cell">Kolektor</th>
              <th className="px-3 py-2 whitespace-nowrap">Tgl Mulai</th>
              <th className="px-3 py-2">Jatuh Tempo</th>
              <th className="px-3 py-2 text-right">Sisa Tagihan</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const pkg = packageMap[c.package_id];
              return (
              <tr key={c.customer_id} className="border-b hover:bg-[#F8F9FA]"
                  data-testid={`billing-cust-row-${c.customer_id}`}>
                {canManage && isAdmin && (
                  <td className="px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      aria-label={`Pilih ${c.nama_customer}`}
                      data-testid={`billing-cust-select-${c.customer_id}`}
                      checked={selected.has(c.customer_id)}
                      onChange={(e) => {
                        setSelected((s) => {
                          const next = new Set(s);
                          if (e.target.checked) next.add(c.customer_id);
                          else next.delete(c.customer_id);
                          return next;
                        });
                      }}
                    />
                  </td>
                )}
                <td className="px-3 py-2 max-w-[280px]">
                  <div className="font-semibold">{highlightMatch(c.nama_customer, qDeb)}</div>
                  <div className="text-xs text-[#6B7280] font-mono">{highlightMatch(c.customer_id, qDeb)}</div>
                  <div className="text-xs mt-0.5" data-testid={`cust-alamat-${c.customer_id}`}>
                    <span className="text-[#6B7280]">Alamat: </span>
                    {c.alamat
                      ? <span>{c.alamat}</span>
                      : <span className="text-[#9CA3AF] italic">Alamat belum diisi</span>}
                  </div>
                  <div className="text-xs" data-testid={`cust-hp-${c.customer_id}`}>
                    <span className="text-[#6B7280]">HP: </span>
                    {c.no_hp
                      ? <a href={`tel:${(c.no_hp || "").replace(/\s+/g, "")}`}
                           className="font-mono text-[#1D4ED8] hover:underline"
                           data-testid={`cust-hp-link-${c.customer_id}`}>
                          {highlightMatch(c.no_hp, qDeb)}
                        </a>
                      : <span className="text-[#9CA3AF] italic">HP belum diisi</span>}
                  </div>
                  {isValidCoord(c) ? (
                    <div className="text-[10px] mt-0.5 inline-flex items-center gap-1 text-[#065F46] bg-[#D1FAE5] px-1.5 py-0.5 rounded"
                         data-testid={`cust-coord-set-${c.customer_id}`}>
                      <MapPin className="w-3 h-3" /> Koordinat: Sudah Diset
                    </div>
                  ) : (
                    <div className="text-[10px] mt-0.5 inline-flex items-center gap-1 text-[#92400E] bg-[#FEF3C7] px-1.5 py-0.5 rounded"
                         data-testid={`cust-coord-unset-${c.customer_id}`}>
                      <MapPin className="w-3 h-3" /> Belum ada koordinat
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 hidden sm:table-cell text-xs">{c.wilayah_id}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-semibold">{pkg?.nama || c.package_id}</div>
                  <div className="text-[#0055FF] font-mono" data-testid={`cust-harga-${c.customer_id}`}>
                    {pkg ? formatRupiah(pkg.harga) : "—"}
                  </div>
                </td>
                <td className="px-3 py-2 hidden lg:table-cell text-xs" data-testid={`cust-kolektor-${c.customer_id}`}>{c.current_collector_name || "-"}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap font-mono" data-testid={`cust-mulai-${c.customer_id}`}>
                  {c.tanggal_mulai || <span className="text-[#9CA3AF]">—</span>}
                </td>
                <td className="px-3 py-2">
                  <JatuhTempoBadge c={c} testid={`cust-jt-${c.customer_id}`} />
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap"
                    data-testid={`cust-sisa-${c.customer_id}`}>
                  {(c.total_sisa_tagihan || 0) > 0
                    ? <span className="text-[#991B1B] font-bold">{formatRupiah(c.total_sisa_tagihan)}</span>
                    : <span className="text-[#065F46]">Rp 0</span>}
                </td>
                <td className="px-3 py-2">
                  <Badge className={`text-xs ${
                    c.status_layanan === "BERHENTI"
                      ? "bg-red-600 hover:bg-red-700 text-white border-transparent"
                    : c.status_layanan === "ISOLIR"
                      ? "bg-emerald-700 hover:bg-emerald-800 text-white border-transparent"
                    : c.status_layanan === "FREE"
                      ? "bg-yellow-400 hover:bg-yellow-500 text-[#78350F] border-transparent"
                      : ""}`}
                    data-testid={`billing-cust-status-badge-${c.customer_id}`}>
                    {CUST_STATUS_LABEL[c.status_layanan] || c.status_layanan}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  {canManage ? (
                    <div className="inline-flex gap-1">
                      {isValidCoord(c) ? (
                        <a href={mapsUrl(c)}
                           target="_blank" rel="noopener noreferrer"
                           className="h-8 w-8 rounded-md border border-[#E5E7EB] flex items-center justify-center text-[#0055FF] hover:bg-[#F0F4FF]"
                           title="Buka di Google Maps"
                           data-testid={`billing-cust-maps-${c.customer_id}`}>
                          <MapPin className="w-3 h-3" />
                        </a>
                      ) : (
                        <Button size="sm" variant="outline"
                                onClick={() => { setEditing({ ...c }); setOpen(true); }}
                                className="h-8 px-2 border-[#FCD34D] text-[#92400E] hover:bg-[#FEF3C7]"
                                title="Set koordinat via Edit Customer"
                                data-testid={`billing-cust-set-coord-${c.customer_id}`}>
                          <MapPin className="w-3 h-3 mr-1" /> Set
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { setEditing({ ...c }); setOpen(true); }}
                              data-testid={`billing-cust-edit-${c.customer_id}`}
                              title="Edit Customer" aria-label="Edit Customer">
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setStatusEditing(c)}
                              data-testid={`billing-cust-status-${c.customer_id}`}
                              title={c.status_layanan === "AKTIF" ? "Nonaktifkan Customer" : "Aktifkan Customer"}
                              aria-label={c.status_layanan === "AKTIF" ? "Nonaktifkan Customer" : "Aktifkan Customer"}>
                        {c.status_layanan === "AKTIF" ? <Ban className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="outline"
                                onClick={() => setDeleting(c)}
                                className="border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEE2E2]"
                                data-testid={`billing-cust-delete-${c.customer_id}`}
                                title="Hapus Customer" aria-label="Hapus Customer">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ) : isKolektor ? (
                    // Feb 2026 — Kolektor: (a) Maps link jika koordinat
                    // sudah ada, (b) tombol Set/Edit Koordinat untuk
                    // insert atau update. Backend re-validates scope;
                    // kolektor di luar scope → 403.
                    <div className="inline-flex gap-1">
                      {isValidCoord(c) && (
                        <a href={mapsUrl(c)}
                           target="_blank" rel="noopener noreferrer"
                           className="h-8 w-8 rounded-md border border-[#E5E7EB] flex items-center justify-center text-[#0055FF] hover:bg-[#F0F4FF]"
                           title="Buka di Google Maps"
                           data-testid={`billing-cust-maps-${c.customer_id}`}>
                          <MapPin className="w-3 h-3" />
                        </a>
                      )}
                      <Button size="sm" variant="outline"
                              onClick={() => setCoordEditing({ ...c })}
                              className={isValidCoord(c)
                                ? "h-8 px-2 border-[#93C5FD] text-[#1E40AF] hover:bg-[#EFF6FF]"
                                : "h-8 px-2 border-[#FCD34D] text-[#92400E] hover:bg-[#FEF3C7]"}
                              title={isValidCoord(c) ? "Edit Koordinat Customer" : "Set Koordinat Customer"}
                              data-testid={`billing-cust-${isValidCoord(c) ? "edit" : "set"}-coord-${c.customer_id}`}>
                        <MapPin className="w-3 h-3 mr-1" /> {isValidCoord(c) ? "Edit Koordinat" : "Set Koordinat"}
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list (<md) — Aug 2026 responsive rework. Data & aksi
          identik dengan tabel desktop; disajikan sebagai card vertikal
          agar tidak ada horizontal overflow di HP. */}
      <div className="md:hidden space-y-2" data-testid="billing-cust-mobile-list">
        {filtered.map((c) => {
          const pkg = packageMap[c.package_id];
          return (
          <div key={c.customer_id}
                className="border rounded-md bg-white p-3 space-y-2"
                data-testid={`billing-cust-mobile-${c.customer_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{highlightMatch(c.nama_customer, qDeb)}</div>
                <div className="text-[11px] text-[#6B7280] font-mono">{highlightMatch(c.customer_id, qDeb)}</div>
              </div>
              <Badge className={`text-[10px] flex-shrink-0 ${
                c.status_layanan === "BERHENTI"
                  ? "bg-red-600 hover:bg-red-700 text-white border-transparent"
                : c.status_layanan === "ISOLIR"
                  ? "bg-emerald-700 hover:bg-emerald-800 text-white border-transparent"
                : c.status_layanan === "FREE"
                  ? "bg-yellow-400 hover:bg-yellow-500 text-[#78350F] border-transparent"
                  : ""}`}
                data-testid={`billing-cust-mobile-status-badge-${c.customer_id}`}>
                {CUST_STATUS_LABEL[c.status_layanan] || c.status_layanan}
              </Badge>
            </div>
            <div className="text-xs space-y-1">
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Alamat</span>
                {c.alamat
                  ? <span className="truncate max-w-[65%] text-right" data-testid={`cust-mobile-alamat-${c.customer_id}`}>{c.alamat}</span>
                  : <span className="text-[#9CA3AF] italic text-right" data-testid={`cust-mobile-alamat-${c.customer_id}`}>Alamat belum diisi</span>}
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">No HP</span>
                {c.no_hp
                  ? <a href={`tel:${(c.no_hp || "").replace(/\s+/g, "")}`}
                       className="font-mono truncate text-[#1D4ED8] hover:underline"
                       data-testid={`cust-mobile-hp-link-${c.customer_id}`}>
                      {highlightMatch(c.no_hp, qDeb)}
                    </a>
                  : <span className="text-[#9CA3AF] italic" data-testid={`cust-mobile-hp-${c.customer_id}`}>HP belum diisi</span>}
              </div>
              <div className="flex justify-between gap-2 items-center">
                <span className="text-[#6B7280]">Koordinat</span>
                {isValidCoord(c) ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#065F46] bg-[#D1FAE5] px-1.5 py-0.5 rounded"
                        data-testid={`cust-mobile-coord-set-${c.customer_id}`}>
                    <MapPin className="w-3 h-3" /> Sudah Diset
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#92400E] bg-[#FEF3C7] px-1.5 py-0.5 rounded"
                        data-testid={`cust-mobile-coord-unset-${c.customer_id}`}>
                    <MapPin className="w-3 h-3" /> Belum Diset
                  </span>
                )}
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Tgl Mulai</span>
                <span className="font-mono">{c.tanggal_mulai || "—"}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Paket</span>
                <span className="truncate max-w-[65%] text-right font-semibold">{pkg?.nama || c.package_id}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Harga Paket</span>
                <span className="font-mono text-[#0055FF]" data-testid={`cust-mobile-harga-${c.customer_id}`}>
                  {pkg ? formatRupiah(pkg.harga) : "—"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Kolektor</span>
                <span className="truncate max-w-[65%] text-right">{c.current_collector_name || "—"}</span>
              </div>
              <div className="flex justify-between gap-2 items-center">
                <span className="text-[#6B7280]">Jatuh Tempo</span>
                <JatuhTempoBadge c={c} testid={`cust-mobile-jt-${c.customer_id}`} />
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Sisa Tagihan</span>
                <span className="font-mono font-bold"
                      data-testid={`cust-mobile-sisa-${c.customer_id}`}>
                  {(c.total_sisa_tagihan || 0) > 0
                    ? <span className="text-[#991B1B]">{formatRupiah(c.total_sisa_tagihan)}</span>
                    : <span className="text-[#065F46]">Rp 0</span>}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-[#6B7280]">Wilayah</span>
                <span className="truncate max-w-[65%] text-right">{c.wilayah_id}</span>
              </div>
            </div>
            {canManage ? (
              <div className="flex gap-2 pt-1 border-t flex-wrap">
                <Button size="sm" variant="outline" className="flex-1"
                        onClick={() => { setEditing({ ...c }); setOpen(true); }}
                        data-testid={`billing-cust-mobile-edit-${c.customer_id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="flex-1"
                        onClick={() => setStatusEditing(c)}
                        data-testid={`billing-cust-mobile-status-${c.customer_id}`}>
                  {c.status_layanan === "AKTIF"
                    ? <><Ban className="w-3 h-3 mr-1" /> Berhentikan</>
                    : <><CheckCircle className="w-3 h-3 mr-1" /> Aktifkan</>}
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="outline"
                          onClick={() => setDeleting(c)}
                          className="border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEE2E2] px-3"
                          data-testid={`billing-cust-mobile-delete-${c.customer_id}`}
                          title="Hapus Customer" aria-label="Hapus Customer">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
                {isValidCoord(c) ? (
                  <a href={mapsUrl(c)}
                     target="_blank" rel="noopener noreferrer"
                     className="h-9 px-3 rounded-md border border-[#E5E7EB] flex items-center justify-center text-xs text-[#0055FF] hover:bg-[#F0F4FF]"
                     title="Buka Google Maps"
                     data-testid={`billing-cust-mobile-maps-${c.customer_id}`}>
                    <MapPin className="w-3 h-3 mr-1" /> Peta
                  </a>
                ) : (
                  <Button size="sm" variant="outline"
                          onClick={() => { setEditing({ ...c }); setOpen(true); }}
                          className="border-[#FCD34D] text-[#92400E] hover:bg-[#FEF3C7]"
                          data-testid={`billing-cust-mobile-set-coord-${c.customer_id}`}
                          title="Set koordinat via Edit Customer">
                    <MapPin className="w-3 h-3 mr-1" /> Set Koordinat
                  </Button>
                )}
              </div>
            ) : isKolektor ? (
              // Feb 2026 — mobile aksi Kolektor: Peta (kalau koord ada)
              // + Set/Edit Koordinat.
              <div className="flex gap-2 pt-1 border-t flex-wrap">
                {isValidCoord(c) && (
                  <a href={mapsUrl(c)}
                     target="_blank" rel="noopener noreferrer"
                     className="h-9 px-3 rounded-md border border-[#E5E7EB] flex items-center justify-center text-xs text-[#0055FF] hover:bg-[#F0F4FF]"
                     title="Buka Google Maps"
                     data-testid={`billing-cust-mobile-maps-${c.customer_id}`}>
                    <MapPin className="w-3 h-3 mr-1" /> Peta
                  </a>
                )}
                <Button size="sm" variant="outline" className="flex-1"
                        onClick={() => setCoordEditing({ ...c })}
                        data-testid={`billing-cust-mobile-${isValidCoord(c) ? "edit" : "set"}-coord-${c.customer_id}`}
                        title={isValidCoord(c) ? "Edit Koordinat Customer" : "Set Koordinat Customer"}>
                  <MapPin className="w-3 h-3 mr-1" /> {isValidCoord(c) ? "Edit Koordinat" : "Set Koordinat"}
                </Button>
              </div>
            ) : null}
          </div>
          );
        })}
      </div>

      {open && (
        <CustomerFormDialog
          open={open}
          onClose={() => { setOpen(false); setEditing(null); }}
          editing={editing}
          wilayahList={wilayahList}
          packageList={packageList}
          kolektorList={kolektorList}
          onSaved={async () => { setOpen(false); setEditing(null); await load(); }}
        />
      )}
      {coordEditing && (
        <CoordinatesDialog
          customer={coordEditing}
          onClose={() => setCoordEditing(null)}
          onSaved={async () => { setCoordEditing(null); await load(); }}
        />
      )}
      {statusEditing && (
        <StatusChangeDialog
          customer={statusEditing}
          onClose={() => setStatusEditing(null)}
          onSaved={async () => { setStatusEditing(null); await load(); }}
        />
      )}
      {bulkReassignOpen && (
        <BulkReassignDialog
          selectedIds={Array.from(selected)}
          kolektorList={kolektorList}
          onClose={() => setBulkReassignOpen(false)}
          onSaved={async () => { setBulkReassignOpen(false); await load(); }}
        />
      )}
      {bulkStartOpen && (
        <BulkStartDateDialog
          selectedRows={rows.filter((r) => selected.has(r.customer_id))}
          onClose={() => setBulkStartOpen(false)}
          onSaved={load}
        />
      )}

      {/* Sep 2026 Phase 20 — Hard delete confirmation */}
      <Dialog open={!!deleting} onOpenChange={(v) => !v && !deleteBusy && setDeleting(null)}>
        <DialogContent className="max-w-md" data-testid="cust-hard-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#991B1B]">
              <Trash2 className="w-5 h-5" /> Hapus Customer Permanen?
            </DialogTitle>
          </DialogHeader>
          {deleting && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[10px] uppercase font-bold text-[#6B7280]">Customer</div>
                <div className="font-semibold">{deleting.nama_customer}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-[#6B7280]">Customer ID</div>
                <div className="font-mono">{deleting.customer_id}</div>
              </div>
              <div className="border-l-2 border-[#DC2626] pl-3 bg-[#FEF2F2] py-2 rounded-r">
                <div className="font-bold text-[#991B1B] mb-1">Peringatan</div>
                <div className="text-xs text-[#7F1D1D]">
                  Customer, seluruh invoice, dan seluruh pembayaran terkait akan
                  dihapus <b>permanen</b>. Tindakan ini tidak dapat dibatalkan.
                </div>
              </div>
              {(deleting.total_sisa_tagihan > 0) && (
                <div className="text-xs bg-[#FEF3C7] border border-[#FDE68A] rounded p-2 text-[#92400E]">
                  Customer masih memiliki outstanding <b>{formatRupiah(deleting.total_sisa_tagihan)}</b>.
                  Riwayat tagihan tersebut juga akan hilang.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}
                    data-testid="cust-hard-delete-cancel">
              Batal
            </Button>
            <Button onClick={runHardDelete} disabled={deleteBusy}
                    className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                    data-testid="cust-hard-delete-confirm">
              {deleteBusy ? "Menghapus…" : "Hapus Permanen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CustomerFormDialog({ open, onClose, editing, wilayahList, packageList, kolektorList = [], onSaved }) {
  const [form, setForm] = useState({
    nama_customer: "", no_hp: "", alamat: "",
    wilayah_id: "", package_id: "",
    latitude: "", longitude: "", google_maps_url: "",
    tanggal_mulai: "", jatuh_tempo: 25, catatan: "",
    collector_user_id: "",  // "" = keep current on edit; "" on create = KOLEKTOR-SYSTEM
  });
  const [saving, setSaving] = useState(false);
  // Feb 2026 — Inline validation errors untuk 3 field wajib: alamat,
  // wilayah, paket. Native `required` tidak catch alamat yang hanya
  // spasi, dan browser-bubble UX kurang jelas untuk <select>. Error
  // dihapus otomatis saat user mulai memperbaiki field terkait.
  const [errors, setErrors] = useState({});
  const alamatRef = useRef(null);
  const wilayahRef = useRef(null);
  const paketRef = useRef(null);
  // Feb 2026 — Validasi KHUSUS Tambah Customer: Kolektor wajib dipilih
  // dari daftar kolektor lapangan. "Kolektor Sistem" ("" default dan
  // nilai eksplisit "KOLEKTOR-SYSTEM") tidak boleh dipakai sebagai
  // assignment awal customer baru.
  const kolektorRef = useRef(null);
  const isNew = editing?.isNew;
  const { user: authUser } = useAuth();

  useEffect(() => {
    if (!editing) return;
    // Aug 2026 — default cerdas: Tanggal Mulai = hari ini (dari editing yg
    // di-pass parent) & Jatuh Tempo = 25 untuk customer baru. Existing
    // customer memakai nilai simpanan.
    setForm({
      nama_customer: editing.nama_customer || "",
      no_hp: editing.no_hp || "",
      alamat: editing.alamat || "",
      wilayah_id: editing.wilayah_id || "",
      package_id: editing.package_id || "",
      latitude: editing.latitude ?? "",
      longitude: editing.longitude ?? "",
      google_maps_url: editing.google_maps_url || "",
      tanggal_mulai: editing.tanggal_mulai || "",
      jatuh_tempo: editing.jatuh_tempo || 25,
      catatan: editing.catatan || "",
      collector_user_id: editing.current_collector_user_id || "",
    });
    setErrors({});
  }, [editing]);

  // Aug 2026 — Harga Paket ditampilkan otomatis dari master paket saat
  // Admin memilih paket. Ini reuse `packageList` yang sudah dimuat parent.
  const selectedPackage = packageList.find((p) => p.package_id === form.package_id);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    // Feb 2026 — Validasi explicit untuk 3 field wajib. trim() di
    // alamat supaya input spasi tidak lolos native `required`. Set
    // errors, fokus ke field pertama yang invalid, dan short-circuit
    // sebelum API call supaya tidak ada network request yang invalid.
    const nextErrors = {};
    if (!(form.alamat || "").trim()) nextErrors.alamat = "Alamat wajib diisi.";
    if (!form.wilayah_id) nextErrors.wilayah_id = "Wilayah wajib dipilih.";
    if (!form.package_id) nextErrors.package_id = "Paket wajib dipilih.";
    // Feb 2026 — Kolektor wajib untuk CREATE. Pada EDIT, "" diinterpret
    // sebagai "jangan swap kolektor" (behavior existing dipertahankan).
    if (isNew) {
      const cid = form.collector_user_id;
      if (!cid || cid === "KOLEKTOR-SYSTEM") {
        nextErrors.collector_user_id = "Kolektor wajib dipilih.";
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstRef = nextErrors.alamat ? alamatRef.current
                      : nextErrors.wilayah_id ? wilayahRef.current
                      : nextErrors.package_id ? paketRef.current
                      : kolektorRef.current;
      if (firstRef) {
        try { firstRef.focus({ preventScroll: false }); } catch {}
        try { firstRef.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
      }
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.latitude === "") delete payload.latitude;
      else payload.latitude = parseFloat(payload.latitude);
      if (payload.longitude === "") delete payload.longitude;
      else payload.longitude = parseFloat(payload.longitude);
      if (payload.jatuh_tempo) payload.jatuh_tempo = parseInt(payload.jatuh_tempo, 10);
      if (!payload.tanggal_mulai) delete payload.tanggal_mulai;
      // Feb 2026 — collector_user_id: kosong pada create → backend
      // otomatis pakai KOLEKTOR-SYSTEM. Pada edit, kosong = tidak swap.
      if (isNew) {
        if (!payload.collector_user_id) delete payload.collector_user_id;
      } else {
        if (!payload.collector_user_id) delete payload.collector_user_id;
      }
      if (isNew) {
        await api.post("/billing/customers", payload);
        toast.success("Customer berhasil dibuat");
      } else {
        await api.patch(`/billing/customers/${editing.customer_id}`, payload);
        toast.success("Customer berhasil diperbarui");
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="w-[95vw] max-w-lg p-4 sm:p-6 max-h-[calc(100dvh-5.5rem)] overflow-y-auto max-lg:top-[calc(50%+2rem)] lg:max-h-[92dvh]"
        data-testid="billing-cust-form">
        <DialogHeader>
          <DialogTitle>{isNew ? "Tambah Customer" : "Edit Customer"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm" noValidate>
          <div><Label>Nama Customer</Label>
            <Input value={form.nama_customer} onChange={(e) => setForm({ ...form, nama_customer: e.target.value })} required minLength={2} data-testid="cust-form-nama" /></div>
          <div><Label>No HP</Label>
            <Input value={form.no_hp} onChange={(e) => setForm({ ...form, no_hp: e.target.value })} data-testid="cust-form-hp" /></div>
          <div><Label>Alamat</Label>
            <Textarea rows={2} value={form.alamat}
                      ref={alamatRef}
                      onChange={(e) => {
                        setForm({ ...form, alamat: e.target.value });
                        if (errors.alamat) setErrors((er) => ({ ...er, alamat: undefined }));
                      }}
                      required minLength={2}
                      className={errors.alamat ? "border-red-400 focus:ring-red-300" : ""}
                      data-testid="cust-form-alamat" />
            {errors.alamat && (
              <p className="text-xs text-red-600 mt-1" data-testid="cust-form-alamat-err">{errors.alamat}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Wilayah</Label>
              <select value={form.wilayah_id}
                      ref={wilayahRef}
                      onChange={(e) => {
                        setForm({ ...form, wilayah_id: e.target.value });
                        if (errors.wilayah_id) setErrors((er) => ({ ...er, wilayah_id: undefined }));
                      }}
                      required
                      className={`border rounded-md px-3 py-2 w-full text-sm bg-white ${errors.wilayah_id ? "border-red-400 focus:ring-red-300" : ""}`}
                      data-testid="cust-form-wilayah">
                <option value="">Pilih…</option>
                {wilayahList.map((w) => <option key={w.wilayah_id} value={w.wilayah_id}>{w.nama_wilayah}</option>)}
              </select>
              {errors.wilayah_id && (
                <p className="text-xs text-red-600 mt-1" data-testid="cust-form-wilayah-err">{errors.wilayah_id}</p>
              )}
            </div>
            <div><Label>Paket</Label>
              <select value={form.package_id}
                      ref={paketRef}
                      onChange={(e) => {
                        setForm({ ...form, package_id: e.target.value });
                        if (errors.package_id) setErrors((er) => ({ ...er, package_id: undefined }));
                      }}
                      required
                      className={`border rounded-md px-3 py-2 w-full text-sm bg-white ${errors.package_id ? "border-red-400 focus:ring-red-300" : ""}`}
                      data-testid="cust-form-paket">
                <option value="">Pilih…</option>
                {packageList.map((p) => <option key={p.package_id} value={p.package_id}>{p.nama_paket} — {formatRupiah(p.harga)}</option>)}
              </select>
              {errors.package_id && (
                <p className="text-xs text-red-600 mt-1" data-testid="cust-form-paket-err">{errors.package_id}</p>
              )}
            </div>
          </div>
          {/* Aug 2026 — Harga Paket otomatis dari master. Read-only: harga
              bulanan tidak boleh diketik manual. Untuk mengubah harga,
              admin harus lewat master Paket (Master Billing). */}
          <div>
            <Label>Harga Paket (otomatis)</Label>
            <div
              data-testid="cust-form-harga-display"
              className="border rounded-md px-3 py-2 w-full text-sm bg-[#F8F9FA] text-[#4B5563] font-mono min-h-[38px] flex items-center">
              {selectedPackage ? (
                <span className="text-[#0055FF] font-bold">{formatRupiah(selectedPackage.harga)}</span>
              ) : (
                <span className="text-[#9CA3AF]">Pilih paket terlebih dahulu</span>
              )}
            </div>
            <div className="text-[10px] text-[#6B7280] mt-1">
              Harga mengikuti master paket. Ubah di menu Master Billing → Paket bila perlu.
            </div>
          </div>
          <div>
            <Label>Kolektor</Label>
            <select value={form.collector_user_id}
                    ref={kolektorRef}
                    onChange={(e) => {
                      setForm({ ...form, collector_user_id: e.target.value });
                      if (errors.collector_user_id) setErrors((er) => ({ ...er, collector_user_id: undefined }));
                    }}
                    className={`border rounded-md px-3 py-2 w-full text-sm bg-white ${errors.collector_user_id ? "border-red-400 focus:ring-red-300" : ""}`}
                    data-testid="cust-form-kolektor">
              {isNew ? (
                <option value="">Pilih kolektor…</option>
              ) : (
                <option value="">Kolektor Sistem (belum ada kolektor lapangan)</option>
              )}
              {!isNew && <option value="KOLEKTOR-SYSTEM">Kolektor Sistem</option>}
              {kolektorList.map((u) => (
                <option key={u.id} value={u.id}>{u.name} (@{u.username}) — {u.role}{(u.extra_roles || []).includes("kolektor") && u.role !== "kolektor" ? " + kolektor" : ""}</option>
              ))}
            </select>
            {errors.collector_user_id && (
              <p className="text-xs text-red-600 mt-1" data-testid="cust-form-kolektor-err">{errors.collector_user_id}</p>
            )}
            <div className="text-[10px] text-[#6B7280] mt-1">
              {isNew ? "Wajib pilih kolektor lapangan untuk customer baru." : "Kosongkan bila tidak ingin mengganti kolektor."}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Latitude</Label>
              <Input value={form.latitude} readOnly tabIndex={-1}
                     className="bg-[#F3F4F6] cursor-not-allowed"
                     placeholder="Ditentukan oleh marker peta"
                     data-testid="cust-form-latitude" /></div>
            <div><Label>Longitude</Label>
              <Input value={form.longitude} readOnly tabIndex={-1}
                     className="bg-[#F3F4F6] cursor-not-allowed"
                     placeholder="Ditentukan oleh marker peta"
                     data-testid="cust-form-longitude" /></div>
          </div>
          <div data-testid="cust-form-map-wrapper">
            <Label>Lokasi (Peta) — geser marker untuk ubah</Label>
            <LocationPicker
              value={form.wilayah || ""}
              lat={form.latitude === "" || form.latitude === null || form.latitude === undefined ? null : Number(form.latitude)}
              lng={form.longitude === "" || form.longitude === null || form.longitude === undefined ? null : Number(form.longitude)}
              onChange={({ lat: l1, lng: l2, wilayah: w }) => {
                setForm((f) => ({
                  ...f,
                  latitude: (l1 !== null && l1 !== undefined) ? String(l1) : f.latitude,
                  longitude: (l2 !== null && l2 !== undefined) ? String(l2) : f.longitude,
                  // Hanya update wilayah kalau customer belum punya nilai
                  // supaya wilayah admin tidak tertimpa
                  wilayah: (!f.wilayah && w) ? w : f.wilayah,
                }));
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Tanggal Mulai</Label>
              <Input type="date" value={form.tanggal_mulai}
                     onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })}
                     data-testid="cust-form-tanggal-mulai" /></div>
            <div><Label>Jatuh Tempo (tgl 1-28)</Label>
              <Input type="number" min="1" max="28" value={form.jatuh_tempo}
                     onChange={(e) => setForm({ ...form, jatuh_tempo: e.target.value })}
                     data-testid="cust-form-jatuh-tempo" /></div>
          </div>
          <div><Label>Catatan</Label>
            <Textarea rows={2} value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} /></div>
          {!isNew && editing?.customer_id && authUser?.role === "admin" && <CustomerOdpSection customerId={editing.customer_id} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="cust-form-submit">
              {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function StatusChangeDialog({ customer, onClose, onSaved }) {
  // Feb 2026 — Status ISOLIR ditambah. Default: bila AKTIF → BERHENTI,
  // bila non-AKTIF → AKTIF.
  const [status, setStatus] = useState(customer.status_layanan === "AKTIF" ? "BERHENTI" : "AKTIF");
  const [tglBerhenti, setTglBerhenti] = useState(new Date().toISOString().slice(0, 10));
  const [alasan, setAlasan] = useState("");
  const [saving, setSaving] = useState(false);
  const needTgl = status === "BERHENTI" || status === "ISOLIR";

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const body = { status_layanan: status };
      if (needTgl) {
        body.tanggal_berhenti = tglBerhenti || new Date().toISOString().slice(0, 10);
        body.alasan_berhenti = alasan;
      }
      await api.post(`/billing/customers/${customer.customer_id}/status`, body);
      toast.success("Status customer diperbarui");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal ubah status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cust-status-dialog">
        <DialogHeader>
          <DialogTitle>Ubah Status Layanan</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="text-xs">Customer: <b>{customer.nama_customer}</b> ({customer.customer_id})</div>
          <div><Label>Status</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    data-testid="cust-status-select"
                    className="border rounded-md px-3 py-2 w-full text-sm bg-white">
              <option value="AKTIF">Aktif</option>
              <option value="ISOLIR">Isolir</option>
              <option value="BERHENTI">Berhenti</option>
              <option value="FREE">Free</option>
            </select></div>
          {needTgl && (
            <>
              <div><Label>Tanggal {status === "ISOLIR" ? "Isolir" : "Berhenti"}</Label>
                <Input type="date" value={tglBerhenti} onChange={(e) => setTglBerhenti(e.target.value)} required data-testid="cust-status-tanggal" /></div>
              <div><Label>Alasan</Label>
                <Textarea rows={2} value={alasan} onChange={(e) => setAlasan(e.target.value)} data-testid="cust-status-alasan" /></div>
            </>
          )}
          <div className="text-xs bg-[#FEF3C7] border border-[#FDE68A] rounded p-2 text-[#92400E]">
            {status === "ISOLIR"
              ? "Customer yang di-isolir (karena tunggakan) tidak akan dibuatkan invoice baru. Invoice lama, tunggakan, dan riwayat pembayaran tetap tersimpan."
              : "Customer yang berhenti tidak akan dibuatkan invoice baru. Invoice lama, tunggakan, dan riwayat pembayaran tetap tersimpan."}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="cust-status-submit">{saving ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 — Dialog "Set Koordinat" khusus (lat/lng only). Digunakan
// KOLEKTOR untuk melengkapi/memperbarui koordinat customer dalam scope
// mereka tanpa membuka full Edit Customer form (yang butuh admin).
// Reuse LocationPicker existing + endpoint dedicated
// `POST /billing/customers/{cid}/coordinates` yang re-validate scope
// di backend.
function CoordinatesDialog({ customer, onClose, onSaved }) {
  // Feb 2026 — Dialog reusable untuk SET (customer belum punya koord)
  // maupun EDIT (sudah ada koord — geser marker / gunakan lokasi). Judul
  // dialog auto-switch berdasarkan `hasExisting`. Backend endpoint sama.
  const hasExisting = customer.latitude !== null && customer.latitude !== undefined &&
                      customer.longitude !== null && customer.longitude !== undefined;
  const [lat, setLat] = useState(hasExisting ? String(customer.latitude) : "");
  const [lng, setLng] = useState(hasExisting ? String(customer.longitude) : "");
  // Feb 2026 — Enhancement: Kolektor dapat memperbaiki alamat sekaligus.
  // Pre-fill dari `customer.alamat` existing. Dikirim hanya jika berubah
  // supaya endpoint tetap no-op kalau user hanya mengoreksi koordinat.
  const initialAlamat = (customer.alamat || "").trim();
  const [alamat, setAlamat] = useState(initialAlamat);
  const [busy, setBusy] = useState(false);
  const [usingCurrent, setUsingCurrent] = useState(false);
  // Feb 2026 — Reverse-geocode on-demand. Opt-in button supaya alamat
  // yang di-ketik manual tidak di-overwrite tanpa izin user. Reuse
  // Nominatim endpoint publik yang sudah dipakai LocationPicker.
  const [reverseLoading, setReverseLoading] = useState(false);

  const reverseFromMap = async () => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      toast.error("Pilih titik pada peta terlebih dahulu.");
      return;
    }
    setReverseLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latNum}&lon=${lngNum}&zoom=18&addressdetails=1&accept-language=id`;
      const res = await fetch(url, { headers: { "Accept": "application/json" } });
      if (!res.ok) throw new Error("reverse-fail");
      const data = await res.json();
      // Nominatim mengembalikan `display_name` sebagai string alamat lengkap
      // dari jalan → kelurahan → kecamatan → kabupaten → provinsi → negara.
      // Kita trim ke batas backend (500 char) agar validasi tidak gagal.
      const displayName = (data?.display_name || "").trim().slice(0, 500);
      if (!displayName) {
        toast.warning("Alamat tidak tersedia untuk titik ini.");
        return;
      }
      setAlamat(displayName);
      toast.success("Alamat di-isi dari peta — koreksi bila perlu.");
    } catch (err) {
      toast.error("Gagal mengambil alamat dari peta.");
    } finally {
      setReverseLoading(false);
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Browser tidak mendukung geolokasi.");
      return;
    }
    setUsingCurrent(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        setUsingCurrent(false);
        toast.success("Lokasi sekarang diterapkan pada peta.");
      },
      (err) => {
        setUsingCurrent(false);
        toast.error(`Gagal ambil lokasi: ${err.message || "unknown"}`);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) {
      toast.error("Koordinat belum valid — pilih titik pada peta.");
      return;
    }
    const trimmedAlamat = (alamat || "").trim();
    const alamatChanged = trimmedAlamat !== initialAlamat;
    if (alamatChanged && trimmedAlamat.length < 2) {
      toast.error("Alamat minimal 2 karakter.");
      return;
    }
    setBusy(true);
    try {
      // Payload backward-compatible: alamat hanya dikirim bila berubah
      // supaya backend tidak men-trigger audit `address_updated` untuk
      // no-op dan request lama yang cuma lat/lng tetap valid.
      const payload = { latitude: latNum, longitude: lngNum };
      if (alamatChanged) payload.alamat = trimmedAlamat;
      await api.post(`/billing/customers/${customer.customer_id}/coordinates`, payload);
      toast.success("Perubahan berhasil disimpan.");
      await onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gagal menyimpan perubahan.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="w-[95vw] max-w-2xl p-4 sm:p-6 max-h-[calc(100dvh-5.5rem)] overflow-y-auto max-lg:top-[calc(50%+2rem)] lg:max-h-[92dvh]" data-testid="cust-coord-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <MapPin className="w-5 h-5 shrink-0 text-[#0055FF]" /> Edit Alamat &amp; Koordinat Customer
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="rounded-md bg-[#F8FAFC] border border-[#E5E7EB] px-3 py-2">
            <div className="text-[10px] font-bold uppercase text-[#6B7280]">Customer</div>
            <div className="font-semibold">{customer.nama_customer}</div>
            <div className="text-xs font-mono text-[#6B7280]">{customer.customer_id}</div>
          </div>
          {/* Feb 2026 — Alamat editable (Admin + Kolektor dalam scope).
              Backend re-validate RBAC & scope sebelum update. Field
              reuse existing `billing_customers.alamat` (tidak ada
              kolom alamat baru). */}
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="cust-coord-alamat">Alamat Customer</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={reverseFromMap}
                disabled={busy || reverseLoading}
                className="h-7 px-2 text-xs"
                data-testid="cust-coord-alamat-reverse"
                title="Isi otomatis dari titik pada peta (Nominatim)"
              >
                <MapPin className="w-3 h-3 mr-1" />
                {reverseLoading ? "Memuat…" : "Isi dari Peta"}
              </Button>
            </div>
            <Textarea
              id="cust-coord-alamat"
              rows={2}
              value={alamat}
              onChange={(e) => setAlamat(e.target.value)}
              placeholder="Alamat lengkap customer"
              minLength={2}
              maxLength={500}
              disabled={busy}
              data-testid="cust-coord-alamat"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm"
                    onClick={useCurrentLocation}
                    disabled={usingCurrent || busy}
                    data-testid="cust-coord-use-current">
              <MapPin className="w-3 h-3 mr-1" /> {usingCurrent ? "Mengambil GPS…" : "Gunakan Lokasi Saya"}
            </Button>
            <span className="text-[11px] text-[#6B7280]">atau geser marker peta di bawah.</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Latitude</Label>
              <Input value={lat} readOnly tabIndex={-1}
                     className="bg-[#F3F4F6] cursor-not-allowed"
                     placeholder="Ditentukan oleh peta"
                     data-testid="cust-coord-lat" />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input value={lng} readOnly tabIndex={-1}
                     className="bg-[#F3F4F6] cursor-not-allowed"
                     placeholder="Ditentukan oleh peta"
                     data-testid="cust-coord-lng" />
            </div>
          </div>
          <div data-testid="cust-coord-map-wrapper">
            <Label>Lokasi (Peta) — geser marker untuk ubah</Label>
            <LocationPicker
              value={customer.wilayah || ""}
              lat={lat === "" ? null : Number(lat)}
              lng={lng === "" ? null : Number(lng)}
              onChange={({ lat: l1, lng: l2 }) => {
                if (l1 !== null && l1 !== undefined) setLat(String(l1));
                if (l2 !== null && l2 !== undefined) setLng(String(l2));
              }}
            />
          </div>
          <DialogFooter className="sticky -bottom-4 sm:-bottom-6 z-[500] -mx-4 sm:-mx-6 gap-2 border-t bg-white px-4 sm:px-6 py-3" data-testid="cust-coord-footer">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
            <Button type="submit" disabled={busy || !lat || !lng} data-testid="cust-coord-submit">
              {busy ? "Menyimpan…" : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function BulkReassignDialog({ selectedIds, kolektorList, onClose, onSaved }) {
  // Feb 2026 — Admin tool: pindahkan N customer sekaligus ke satu
  // kolektor. Hitting POST /billing/assignments/bulk-reassign yang
  // melakukan close current + create new assignment per-customer di
  // backend (audit event per-customer tetap terbit).
  const [targetId, setTargetId] = useState("");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const count = selectedIds.length;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!targetId) {
      setError("Pilih kolektor tujuan terlebih dahulu.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/assignments/bulk-reassign", {
        customer_ids: selectedIds,
        collector_user_id: targetId,
        catatan: catatan.trim() || undefined,
      });
      const r = data?.reassigned_count ?? 0;
      const s = data?.skipped_count ?? 0;
      if (r > 0 && s === 0) {
        toast.success(`${r} customer berhasil di-reassign.`);
      } else if (r > 0 && s > 0) {
        toast.success(`${r} customer di-reassign (${s} dilewati karena tidak ditemukan).`);
      } else {
        toast.warning("Tidak ada customer yang berhasil di-reassign.");
      }
      await onSaved?.();
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Gagal reassign kolektor.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="w-[95vw] max-w-md" data-testid="billing-cust-bulk-reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reassign Kolektor — {count} customer</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm" noValidate>
          <div className="rounded-md bg-[#F8FAFC] border border-[#E5E7EB] px-3 py-2">
            <div className="text-[10px] font-bold uppercase text-[#6B7280]">Jumlah dipilih</div>
            <div className="font-semibold">{count} customer akan dipindahkan ke kolektor baru.</div>
          </div>
          <div>
            <Label htmlFor="bulk-reassign-target">Kolektor Tujuan</Label>
            <select
              id="bulk-reassign-target"
              value={targetId}
              onChange={(e) => { setTargetId(e.target.value); if (error) setError(""); }}
              className={`border rounded-md px-3 py-2 w-full text-sm bg-white ${error ? "border-red-400 focus:ring-red-300" : ""}`}
              data-testid="billing-cust-bulk-reassign-target"
              disabled={busy}
            >
              <option value="">Pilih kolektor…</option>
              <option value="KOLEKTOR-SYSTEM">Kolektor Sistem (lepas assignment)</option>
              {kolektorList.map((u) => (
                <option key={u.id} value={u.id}>{u.name} (@{u.username}) — {u.role}</option>
              ))}
            </select>
            {error && (
              <p className="text-xs text-red-600 mt-1" data-testid="billing-cust-bulk-reassign-err">{error}</p>
            )}
          </div>
          <div>
            <Label htmlFor="bulk-reassign-note">Catatan (opsional)</Label>
            <Input
              id="bulk-reassign-note"
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
              placeholder="Alasan reassignment"
              maxLength={200}
              disabled={busy}
              data-testid="billing-cust-bulk-reassign-note"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Batal</Button>
            <Button type="submit" disabled={busy} data-testid="billing-cust-bulk-reassign-submit">
              {busy ? "Memproses…" : `Reassign ${count} customer`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}



const todayWib = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);

function StartDateResult({ result }) {
  return (
    <div className="space-y-2 text-sm" data-testid="bulk-startdate-result">
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-[#ECFDF5] border border-[#A7F3D0] py-2"><div className="text-lg font-black text-[#047857]" data-testid="bulk-startdate-updated">{result.updated_count}</div><div className="text-[10px] uppercase">Berhasil</div></div>
        <div className="rounded-md bg-[#F3F4F6] border py-2"><div className="text-lg font-black text-[#374151]" data-testid="bulk-startdate-unchanged">{result.unchanged_count}</div><div className="text-[10px] uppercase">Tidak berubah</div></div>
        <div className="rounded-md bg-[#FEF2F2] border border-[#FECACA] py-2"><div className="text-lg font-black text-[#B91C1C]" data-testid="bulk-startdate-failed">{result.failed_count}</div><div className="text-[10px] uppercase">Gagal</div></div>
      </div>
      {result.failed?.length > 0 && (
        <ul className="max-h-32 overflow-y-auto text-xs border rounded-md p-2 space-y-0.5" data-testid="bulk-startdate-failed-list">
          {result.failed.map((f) => <li key={f.customer_id}><span className="font-mono">{f.customer_id}</span> — {f.reason}</li>)}
        </ul>
      )}
      <p className="text-[11px] text-[#6B7280]">Invoice, pembayaran, dan tunggakan yang sudah ada tidak diubah.</p>
    </div>
  );
}

function BulkStartDateDialog({ selectedRows: initialRows, onClose, onSaved }) {
  // Okt 2026 — Admin: koreksi massal tanggal_mulai customer terpilih (data customer saja).
  // Snapshot pilihan saat modal dibuka: reload tabel setelah simpan mengosongkan seleksi.
  const [selectedRows] = useState(initialRows);
  const [tgl, setTgl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const count = result
    ? result.updated_count + result.unchanged_count + result.failed_count
    : selectedRows.length;
  const same = tgl ? selectedRows.filter((r) => (r.tanggal_mulai || "") === tgl).length : 0;
  const future = tgl && tgl > todayWib();
  const groups = useMemo(() => {
    const g = {};
    selectedRows.forEach((r) => { const k = r.tanggal_mulai || "—"; g[k] = (g[k] || 0) + 1; });
    return Object.entries(g).sort();
  }, [selectedRows]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(tgl)) { setError("Pilih tanggal mulai baru."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/customers/bulk-start-date", {
        customer_ids: selectedRows.map((r) => r.customer_id), tanggal_mulai: tgl,
      });
      setResult(data);
      if (data.failed_count) toast.warning(`${data.updated_count} berhasil, ${data.failed_count} gagal.`);
      else toast.success(`${data.updated_count} customer diperbarui${data.unchanged_count ? `, ${data.unchanged_count} tidak berubah` : ""}.`);
      await onSaved?.();
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Gagal mengubah tanggal mulai.";
      setError(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="w-[95vw] max-w-lg" data-testid="billing-cust-bulk-startdate-dialog">
        <DialogHeader><DialogTitle data-testid="bulk-startdate-title">Ubah Tanggal Mulai — {count} customer</DialogTitle><DialogDescription className="sr-only">Koreksi massal tanggal mulai layanan customer terpilih</DialogDescription></DialogHeader>
        {result ? (
          <>
            <StartDateResult result={result} />
            <DialogFooter><Button onClick={onClose} data-testid="bulk-startdate-close">Tutup</Button></DialogFooter>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-3 text-sm" noValidate>
            <div className="rounded-md bg-[#F8FAFC] border px-3 py-2">
              <div className="text-[10px] font-bold uppercase text-[#6B7280]">Tanggal mulai saat ini</div>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="bulk-startdate-old-groups">
                {groups.map(([k, n]) => <span key={k} className="font-mono text-xs bg-white border rounded px-1.5 py-0.5">{k} · {n}</span>)}
              </div>
            </div>
            <div>
              <Label htmlFor="bulk-startdate-input">Tanggal Mulai Baru</Label>
              <Input id="bulk-startdate-input" type="date" value={tgl} disabled={busy}
                     onChange={(e) => { setTgl(e.target.value); if (error) setError(""); }}
                     className={error ? "border-red-400" : ""} data-testid="bulk-startdate-input" />
              {error && <p className="text-xs text-red-600 mt-1" data-testid="bulk-startdate-err">{error}</p>}
            </div>
            {tgl && (
              <div className="max-h-36 overflow-y-auto border rounded-md text-xs" data-testid="bulk-startdate-preview">
                {selectedRows.map((r) => (
                  <div key={r.customer_id} className="flex justify-between gap-2 px-2 py-1 border-b last:border-0">
                    <span className="truncate">{r.nama_customer} <span className="font-mono text-[#6B7280]">{r.customer_id}</span></span>
                    <span className="font-mono whitespace-nowrap">{r.tanggal_mulai || "—"} → {(r.tanggal_mulai || "") === tgl ? "tidak berubah" : tgl}</span>
                  </div>
                ))}
              </div>
            )}
            {future && (
              <p className="text-xs text-[#B45309] flex gap-1" data-testid="bulk-startdate-future-warn">
                <AlertTriangle className="w-4 h-4 shrink-0" /> Tanggal berada di masa depan.
              </p>
            )}
            <div className="rounded-md bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 text-xs text-[#92400E] space-y-1" data-testid="bulk-startdate-warning">
              <p>Perubahan berlaku untuk <b>seluruh {count} customer</b> yang dipilih{same ? ` (${same} sudah sama dan akan dilewati)` : ""}.</p>
              <p>Hanya data customer yang diubah. Invoice, riwayat pembayaran, status invoice, sisa tagihan, dan carry-forward yang sudah ada <b>tidak diubah</b>, dan Generate Invoice tidak dijalankan.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="bulk-startdate-cancel">Batal</Button>
              <Button type="submit" disabled={busy} className="bg-[#0D9488] hover:bg-[#0F766E] text-white" data-testid="bulk-startdate-submit">
                {busy ? "Menyimpan…" : "Simpan Perubahan"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
